/* SoloTap storefront behaviour: tap strip, mobile action bar, bottom sheets, quick-buy, activation note. */
(() => {
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const ACTIVATION_PROGRAMMED = 'Programmata da SoloTap';
  const ACTIVATION_GUIDE = 'Video guida via email';

  /* Tap strip: play the short sequence once when it scrolls into view. */
  const initTapStrips = () => {
    const strips = document.querySelectorAll('[data-st-tap]:not([data-st-ready])');
    if (!strips.length || reduceMotion || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-playing');
        io.unobserve(entry.target);
      });
    }, { threshold: 0.6 });
    strips.forEach((strip) => {
      strip.setAttribute('data-st-ready', '');
      io.observe(strip);
    });
  };

  /* Action bar: visible after the hero button scrolls away, hidden over the kits and the footer. */
  const initActionBars = () => {
    if (!('IntersectionObserver' in window)) return;
    document.querySelectorAll('[data-st-actionbar]:not([data-st-ready])').forEach((bar) => {
      bar.setAttribute('data-st-ready', '');
      const trigger = bar.dataset.showAfter ? document.querySelector(bar.dataset.showAfter) : null;
      if (!trigger) return;
      const covers = bar.dataset.hideOver ? Array.from(document.querySelectorAll(bar.dataset.hideOver)) : [];
      let past = false;
      const covering = new Set();
      const update = () => {
        const visible = past && covering.size === 0;
        bar.classList.toggle('is-visible', visible);
        bar.inert = !visible;
        bar.setAttribute('aria-hidden', String(!visible));
      };
      new IntersectionObserver(([entry]) => {
        past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        update();
      }).observe(trigger);
      if (covers.length) {
        const coverIo = new IntersectionObserver((entries) => {
          entries.forEach((entry) => (entry.isIntersecting ? covering.add(entry.target) : covering.delete(entry.target)));
          update();
        });
        covers.forEach((el) => coverIo.observe(el));
      }
      update();
    });
  };

  /* Bottom sheets built on <dialog>: Back button, backdrop tap and Esc all close them. */
  const openSheet = (dialog) => {
    if (!dialog || dialog.open) return;
    dialog.showModal();
    document.documentElement.classList.add('st-locked');
    history.pushState({ stSheet: dialog.id }, '');
    dialog.dataset.historyPushed = '1';
  };
  const closeSheet = (dialog) => {
    if (dialog && dialog.open) dialog.close();
  };
  const initSheets = () => {
    document.querySelectorAll('dialog.st-sheet:not([data-st-ready])').forEach((dialog) => {
      dialog.setAttribute('data-st-ready', '');
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) closeSheet(dialog);
        if (event.target.closest('[data-st-sheet-close]')) closeSheet(dialog);
      });
      dialog.addEventListener('close', () => {
        document.documentElement.classList.remove('st-locked');
        if (dialog.dataset.historyPushed === '1') {
          dialog.dataset.historyPushed = '';
          if (history.state && history.state.stSheet === dialog.id) history.back();
        }
      });
    });
  };
  window.addEventListener('popstate', () => {
    document.querySelectorAll('dialog.st-sheet[open]').forEach((dialog) => {
      dialog.dataset.historyPushed = '';
      dialog.close();
    });
  });

  /* Quick-buy: pick language and optional Google link, then straight to checkout. */
  const findVariant = (kit, lang) => {
    const want = lang === 'Inglese' ? ['ingl', 'engl'] : ['ital'];
    return kit.variants.find((v) => want.some((w) => String(v.option).toLowerCase().includes(w))) || null;
  };

  const initKits = () => {
    document.querySelectorAll('[data-st-kits]:not([data-st-ready])').forEach((section) => {
      section.setAttribute('data-st-ready', '');
      const dataEl = section.querySelector('[data-st-kits-data]');
      const dialog = section.querySelector('dialog.st-sheet');
      if (!dataEl || !dialog) return;
      let kits = {};
      try { kits = JSON.parse(dataEl.textContent).kits || {}; } catch (e) { return; }

      const el = (sel) => dialog.querySelector(sel);
      const thumb = el('[data-st-q="thumb"]');
      const title = el('[data-st-q="title"]');
      const price = el('[data-st-q="price"]');
      const langButtons = Array.from(dialog.querySelectorAll('[data-st-lang]'));
      const mixed = el('[data-st-q="mixed"]');
      const mixedRows = el('[data-st-q="mixed-rows"]');
      const link = el('[data-st-q="link"]');
      const error = el('[data-st-q="error"]');
      const pay = el('[data-st-q="pay"]');
      const toCart = el('[data-st-q="cart"]');
      const state = { kit: null, lang: 'Italiano' };

      const render = () => {
        const kit = state.kit;
        if (!kit) return;
        const variant = findVariant(kit, state.lang) || kit.variants.find((v) => v.available) || kit.variants[0];
        title.textContent = kit.name;
        price.textContent = kit.priceLine;
        const img = (variant && variant.image) || kit.image;
        if (img) { thumb.src = img; thumb.hidden = false; } else { thumb.hidden = true; }
        langButtons.forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.stLang === state.lang)));
        pay.textContent = `${pay.dataset.label} · ${variant ? variant.price : kit.price}`;
      };

      const buildMixed = () => {
        const items = state.kit.items || [];
        mixed.hidden = items.length < 2;
        mixed.open = false;
        mixedRows.innerHTML = '';
        items.forEach((label, i) => {
          const row = document.createElement('label');
          row.className = 'st-mixed__row';
          row.textContent = label;
          const select = document.createElement('select');
          select.className = 'st-select';
          select.id = `${dialog.id}-mixed-${i}`;
          select.dataset.item = label;
          ['Italiano', 'Inglese'].forEach((opt) => {
            const o = document.createElement('option');
            o.value = opt;
            o.textContent = opt;
            select.appendChild(o);
          });
          select.value = state.lang;
          row.appendChild(select);
          mixedRows.appendChild(row);
        });
      };

      section.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-st-buy]');
        if (!trigger) return;
        const kit = kits[trigger.dataset.stBuy];
        if (!kit) return;
        event.preventDefault();
        state.kit = kit;
        state.lang = 'Italiano';
        error.hidden = true;
        link.value = '';
        buildMixed();
        render();
        openSheet(dialog);
      });

      langButtons.forEach((button) => {
        button.addEventListener('click', () => {
          state.lang = button.dataset.stLang;
          mixedRows.querySelectorAll('select').forEach((s) => { s.value = state.lang; });
          render();
        });
      });

      const submit = async (goToCheckout) => {
        const kit = state.kit;
        if (!kit) return;
        const variant = findVariant(kit, state.lang) || kit.variants.find((v) => v.available) || kit.variants[0];
        const properties = {};
        const linkValue = link.value.trim();
        if (linkValue) properties['Link Google Maps'] = linkValue;
        properties.Attivazione = linkValue ? ACTIVATION_PROGRAMMED : ACTIVATION_GUIDE;
        const selects = Array.from(mixedRows.querySelectorAll('select'));
        if (selects.some((s) => s.value !== state.lang)) {
          selects.forEach((s) => { properties[`Lingua ${s.dataset.item}`] = s.value; });
        }
        const button = goToCheckout ? pay : toCart;
        button.setAttribute('aria-busy', 'true');
        error.hidden = true;
        try {
          const res = await fetch(`${root}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: variant.id, quantity: 1, properties }] }),
          });
          if (!res.ok) throw new Error(String(res.status));
          window.location.href = `${root}${goToCheckout ? 'checkout' : 'cart'}`;
        } catch (e) {
          button.removeAttribute('aria-busy');
          error.hidden = false;
        }
      };
      pay.addEventListener('click', () => submit(true));
      toCart.addEventListener('click', () => submit(false));
    });
  };

  /* Product page link field: record whether the order needs the video guide. */
  const initActivation = () => {
    document.querySelectorAll('[data-st-maps]:not([data-st-ready])').forEach((wrap) => {
      wrap.setAttribute('data-st-ready', '');
      const input = wrap.querySelector('[data-st-maps-input]');
      const hidden = wrap.querySelector('[data-st-activation]');
      if (!input || !hidden) return;
      const fieldName = input.getAttribute('name');
      const sync = () => {
        const hasLink = input.value.trim() !== '';
        hidden.value = hasLink ? ACTIVATION_PROGRAMMED : ACTIVATION_GUIDE;
        // An empty link would show up as a blank line on the order, so only send it when filled in.
        if (hasLink) input.setAttribute('name', fieldName);
        else input.removeAttribute('name');
      };
      input.addEventListener('input', sync);
      sync();
    });
  };

  /* Looping clips: load and play only while on screen; no autoplay with reduced motion or data saver. */
  const initLoops = () => {
    const videos = Array.from(document.querySelectorAll('video[data-st-loop]:not([data-st-ready])'));
    if (!videos.length) return;
    const saveData = Boolean(navigator.connection && navigator.connection.saveData);
    videos.forEach((video) => video.setAttribute('data-st-ready', ''));
    if (reduceMotion || saveData || !('IntersectionObserver' in window)) {
      videos.forEach((video) => {
        video.controls = true;
        video.src = video.dataset.src;
      });
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const video = entry.target;
        if (entry.isIntersecting) {
          if (!video.getAttribute('src')) video.src = video.dataset.src;
          const playing = video.play();
          if (playing && playing.catch) playing.catch(() => {});
        } else if (!video.paused) {
          video.pause();
        }
      });
    }, { threshold: 0.35 });
    videos.forEach((video) => io.observe(video));
  };

  const init = () => {
    initTapStrips();
    initActionBars();
    initSheets();
    initKits();
    initActivation();
    initLoops();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
